// ActionDetailTab — the default tab showing action fields with inline editing.
// Actions have: title, description, due date, change_notes (required on save).
// Plus state transition buttons: Start (draft → in_progress), Close (in_progress → closed).

import { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  actionsApi,
  actionLifecycleStateLabels,
  actionLifecycleStateColors,
  type UpdateActionRequest,
} from '@/lib/api/actions'
import { queryKeys } from '@/lib/api/queryKeys'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormSkeleton } from '@/components/shared/LoadingSkeleton'
import { Pencil, Check, X, ArrowRight, Lock, Calendar } from 'lucide-react'
import { cn } from '@/lib/utils'

export function ActionDetailTab() {
  const { slug } = useTenant()
  const { actionId } = useParams({ strict: false }) as { actionId: string }
  const api = useApi()
  const actApi = actionsApi(api)
  const queryClient = useQueryClient()

  const { data: action, isLoading } = useQuery({
    queryKey: queryKeys.actions.detail(slug, actionId),
    queryFn: () => actApi.get(actionId),
  })

  // --- Editing state ---
  const [editing, setEditing] = useState(false)
  const [draftTitle, setDraftTitle] = useState('')
  const [draftDescription, setDraftDescription] = useState('')
  const [draftDueDate, setDraftDueDate] = useState('')
  const [changeNotes, setChangeNotes] = useState('')

  // --- Close dialog state ---
  const [showCloseDialog, setShowCloseDialog] = useState(false)
  const [closureComment, setClosureComment] = useState('')

  // Sync draft state from server data
  useEffect(() => {
    if (action) {
      setDraftTitle(action.title)
      setDraftDescription(action.description ?? '')
      setDraftDueDate(action.due_date ?? '')
    }
  }, [action])

  const isDirty = useMemo(() => {
    if (!action) return false
    return (
      draftTitle !== action.title ||
      (draftDescription || '') !== (action.description ?? '') ||
      (draftDueDate || '') !== (action.due_date ?? '')
    )
  }, [action, draftTitle, draftDescription, draftDueDate])

  const handleCancel = useCallback(() => {
    if (action) {
      setDraftTitle(action.title)
      setDraftDescription(action.description ?? '')
      setDraftDueDate(action.due_date ?? '')
    }
    setChangeNotes('')
    setEditing(false)
  }, [action])

  function invalidateAction() {
    queryClient.invalidateQueries({ queryKey: queryKeys.actions.detail(slug, actionId) })
    queryClient.invalidateQueries({ queryKey: queryKeys.actions.all(slug) })
  }

  // --- Update mutation ---
  const updateMutation = useMutation({
    mutationFn: (req: UpdateActionRequest) => actApi.update(actionId, req),
    onSuccess: () => {
      invalidateAction()
      setChangeNotes('')
      setEditing(false)
    },
  })

  // --- Start mutation ---
  const startMutation = useMutation({
    mutationFn: () => actApi.start(actionId),
    onSuccess: () => invalidateAction(),
  })

  // --- Close mutation ---
  const closeMutation = useMutation({
    mutationFn: () => actApi.close(actionId, { closure_comment: closureComment }),
    onSuccess: () => {
      invalidateAction()
      setShowCloseDialog(false)
      setClosureComment('')
    },
  })

  const isMutating = updateMutation.isPending || startMutation.isPending || closeMutation.isPending

  const handleApply = useCallback(() => {
    if (!action || !changeNotes.trim()) return
    const req: UpdateActionRequest = {
      change_notes: changeNotes.trim(),
    }
    if (draftTitle !== action.title) req.title = draftTitle
    if ((draftDescription || '') !== (action.description ?? ''))
      req.description = draftDescription || undefined
    if ((draftDueDate || '') !== (action.due_date ?? ''))
      req.due_date = draftDueDate || undefined
    updateMutation.mutate(req)
  }, [action, draftTitle, draftDescription, draftDueDate, changeNotes, updateMutation])

  if (isLoading || !action) return <FormSkeleton />

  const isClosed = action.lifecycle_state === 'closed'

  return (
    <div className="space-y-6 p-6">
      {/* Row 1: Core fields */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base">Action Details</CardTitle>
          {!isClosed && !editing && (
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
            </Button>
          )}
          {editing && (
            <div className="flex items-center gap-2">
              {isDirty && <span className="text-xs text-amber-600">Unsaved</span>}
              <Button variant="ghost" size="sm" onClick={handleCancel} disabled={isMutating}>
                <X className="mr-1 h-3.5 w-3.5" /> Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleApply}
                disabled={!isDirty || !changeNotes.trim() || isMutating}
              >
                <Check className="mr-1 h-3.5 w-3.5" /> Apply Changes
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {updateMutation.isError && (
            <div className="rounded bg-destructive/10 p-2 text-sm text-destructive">
              {(updateMutation.error as Error).message || 'Failed to save'}
            </div>
          )}

          <EditableField label="Title" value={draftTitle} onChange={setDraftTitle} editing={editing} />
          <EditableField
            label="Description"
            value={draftDescription}
            onChange={setDraftDescription}
            editing={editing}
            multiline
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Due date */}
            <div>
              <dt className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Due Date
              </dt>
              {editing ? (
                <Input
                  type="date"
                  value={draftDueDate}
                  onChange={(e) => setDraftDueDate(e.target.value)}
                />
              ) : (
                <dd className="text-sm">
                  {action.due_date ? (
                    <span className={cn('flex items-center gap-1', action.is_overdue && 'text-red-600')}>
                      <Calendar className="h-3.5 w-3.5" />
                      {new Date(action.due_date + 'T00:00:00').toLocaleDateString()}
                      {action.is_overdue && (
                        <Badge variant="outline" className="ml-1 px-1 py-0 text-xs text-red-600">
                          Overdue
                        </Badge>
                      )}
                    </span>
                  ) : (
                    <span className="italic text-muted-foreground">Not set</span>
                  )}
                </dd>
              )}
            </div>

            {/* Owner */}
            <ReadField label="Owner" value={action.owner_display_name} />
          </div>

          {/* Change notes — only visible in edit mode */}
          {editing && (
            <div>
              <dt className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Change Notes *
              </dt>
              <Input
                placeholder="Describe what changed and why..."
                value={changeNotes}
                onChange={(e) => setChangeNotes(e.target.value)}
              />
              {isDirty && !changeNotes.trim() && (
                <p className="mt-1 text-xs text-amber-600">
                  Change notes are required when saving changes.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Row 2: Classification & Lifecycle */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Classification & Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <ReadField label="Category" value={action.category_name} />
            <div>
              <dt className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Lifecycle State
              </dt>
              <dd className="flex items-center gap-3">
                <Badge
                  variant="outline"
                  className={cn('text-xs', actionLifecycleStateColors[action.lifecycle_state])}
                >
                  {actionLifecycleStateLabels[action.lifecycle_state]}
                </Badge>
                {action.lifecycle_state === 'draft' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={isMutating}
                    onClick={() => startMutation.mutate()}
                  >
                    <ArrowRight className="mr-1 h-3 w-3" />
                    Start Action
                  </Button>
                )}
                {action.lifecycle_state === 'in_progress' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={isMutating}
                    onClick={() => setShowCloseDialog(true)}
                  >
                    <Lock className="mr-1 h-3 w-3" />
                    Close Action
                  </Button>
                )}
              </dd>
            </div>
            <ReadField label="Created Via" value={action.created_via} />
            <ReadField
              label="Created"
              value={new Date(action.created_at).toLocaleDateString()}
            />
          </div>

          {/* Closure info (only when closed) */}
          {isClosed && (
            <div className="mt-4 rounded-lg border bg-muted/20 p-4">
              <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Closure Information
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {action.closed_at && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5" />
                    Closed {new Date(action.closed_at).toLocaleDateString()}
                  </div>
                )}
              </div>
              {action.closure_comment && (
                <p className="mt-2 text-sm">{action.closure_comment}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Row 3: Summary counts */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
            <SummaryItem label="Linked Risks" value={action.linked_risk_count} />
            <SummaryItem label="Linked Controls" value={action.linked_control_count} />
            <SummaryItem label="Linked Events" value={action.linked_event_count} />
            <SummaryItem label="Updates" value={action.update_count} />
          </div>
        </CardContent>
      </Card>

      {/* Close action dialog */}
      {showCloseDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg border bg-background shadow-lg">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h2 className="text-lg font-semibold">Close Action</h2>
              <button
                className="rounded-sm p-1 hover:bg-accent"
                onClick={() => setShowCloseDialog(false)}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4 px-6 py-4">
              {closeMutation.isError && (
                <div className="rounded bg-destructive/10 p-2 text-sm text-destructive">
                  {(closeMutation.error as Error).message || 'Failed to close action'}
                </div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium">Closure Comment *</label>
                <textarea
                  className="min-h-[80px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="Explain why this action is being closed..."
                  value={closureComment}
                  onChange={(e) => setClosureComment(e.target.value)}
                  autoFocus
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t px-6 py-4">
              <Button variant="outline" onClick={() => setShowCloseDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => closeMutation.mutate()}
                disabled={closeMutation.isPending || !closureComment.trim()}
              >
                {closeMutation.isPending ? 'Closing...' : 'Close Action'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// --- Shared helpers ---

function EditableField({
  label,
  value,
  onChange,
  editing,
  multiline,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  editing: boolean
  multiline?: boolean
}) {
  return (
    <div>
      <dt className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      {editing ? (
        multiline ? (
          <textarea
            className="min-h-[60px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : (
          <Input value={value} onChange={(e) => onChange(e.target.value)} />
        )
      ) : (
        <dd className="text-sm">
          {value || <span className="italic text-muted-foreground">Not set</span>}
        </dd>
      )}
    </div>
  )
}

function ReadField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm">
        {value || <span className="italic text-muted-foreground">Not set</span>}
      </dd>
    </div>
  )
}

function SummaryItem({
  label,
  value,
}: {
  label: string
  value: number | string
}) {
  return (
    <div className="rounded bg-muted/30 p-3 text-center">
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{label}</div>
    </div>
  )
}
